import React, { memo, useMemo, useRef } from "react"
import { Box, Container, Stack, VStack } from "@chakra-ui/react"

import { useConfigContext } from "@app/providers/config"
import { useLocationContext } from "@app/providers/location"
import { useNewsletter } from "@app/hooks/useNewsletter"
import { useInsideDataPage } from "@app/hooks/useInsideData"
import { ProductForm } from "@app/components/Product/Form/ProductForm"
import { ProductGiftcard } from "@app/components/Product/Giftcard/ProductGiftcard"
import { ProductMedia } from "@app/components/Product/Media/ProductMedia"
import { RecentlyViewed } from "@app/components/Product/Recent/RecentlyViewed"
import { SubscribePopup } from "@app/components/Subscribe/SubscribePopup"

import type { Props as GatsbyProps } from "@app/templates/product"
import type { PageProps, ProductProps } from "@root/types/global"
import { ReviewSection } from "./Review/ReviewSection"
import { ProductCurations } from "@app/components/Product/Curations/ProductCurations"
import { LookBookProduct } from "@app/components/Product/LookBook/LookBookProduct"

export type ReviewProps = {
  settingEnableReviews: boolean
}

type Props = Omit<GatsbyProps, "grouped"> & {
  grouped?: Array<ProductProps>
  swatches?: Array<ProductProps>
  pairings?: Array<ProductProps>
  curations?: GatsbyTypes.SanityProductCurations
  lookbook?: GatsbyTypes.SanityLookBookProduct
}

export const Product: React.FC<PageProps<Props>> = memo(
  ({ grouped, swatches, loading, product, pairings, curations, lookbook, location, template }) => {
    const {
      settings: {
        product: { giftCardType },
      },
    } = useConfigContext()
    const { locating } = useLocationContext()
    const { newsletter } = useNewsletter(location)
    const reviewRef = useRef(null)
    const scrollToReview = () => {
      if (reviewRef.current) reviewRef.current.scrollIntoView({ behavior: "smooth" })
    }

    const isLoading = useMemo(() => loading || locating, [loading, locating])
    const isGiftcard = useMemo(() => product?.productType === giftCardType, [giftCardType, product?.productType])
    const item = useMemo(() => product, [product])
    useInsideDataPage(product.title, "product")

    return !isGiftcard ? (
      <>
        <Container px={{ base: 0, xl: 12.5 }} maxWidth="100%" paddingInlineStart="0 !important" paddingInlineEnd="0 !important">
          {newsletter?.enabled === true && newsletter?.templateProduct === true ? <SubscribePopup location={location} /> : ""}
          <Box pb={[12.5, 25, 22.5]}>
            <Stack as="section" flexDirection={["column", "row"]} spacing={[8, 0]}>
              <Box w={["full", "50%"]}>
                <ProductMedia product={item} />
              </Box>
              <VStack
                alignItems="center"
                justifyContent={{ base: "center", sm: "flex-start", md: "flex-start" }}
                pt={{ base: 0, sm: 30 }}
                spacing={11.5}
                w={["full", "50%"]}
              >
                <ProductForm
                  grouped={grouped}
                  swatches={swatches}
                  isLoading={loading}
                  product={product}
                  pairings={pairings}
                  scrollToReview={scrollToReview}
                  template={template}
                />
              </VStack>
            </Stack>
            {lookbook?.products?.length && <LookBookProduct lookbook={lookbook} />}
          </Box>
          {curations?.products?.length && <ProductCurations curations={curations} product={product} />}
        </Container>
        {!isLoading && <RecentlyViewed />}

        {template?.settingEnableReviews && <ReviewSection ref={reviewRef} product={product} swatches={swatches} />}
      </>
    ) : (
      <ProductGiftcard product={product} />
    )
  }
)
